import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";

import { supabase } from "../providers/supabase/supabase";

type DealType = {
  id: number;
  name: string;
  color: string;
  default_duration_months: number;
  default_rate_per_unit: number;
  default_closing_offset_months: number;
  sort_order: number;
};

export const DealTypesConfig = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [types, setTypes] = useState<DealType[]>([]);
  const [dirty, setDirty] = useState(false);

  const { data } = useQuery({
    queryKey: ["deal_types"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deal_types")
        .select("*")
        .order("sort_order");
      if (error) throw error;
      return data as DealType[];
    },
  });

  useEffect(() => {
    if (data) setTypes(data);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      for (const t of types) {
        if (t.id > 0) {
          const { error } = await supabase
            .from("deal_types")
            .update({
              name: t.name,
              color: t.color,
              default_duration_months: t.default_duration_months,
              default_rate_per_unit: t.default_rate_per_unit,
              default_closing_offset_months: t.default_closing_offset_months,
              sort_order: t.sort_order,
            })
            .eq("id", t.id);
          if (error) throw error;
        } else {
          const { error } = await supabase.from("deal_types").insert({
            name: t.name,
            color: t.color,
            default_duration_months: t.default_duration_months,
            default_rate_per_unit: t.default_rate_per_unit,
            default_closing_offset_months: t.default_closing_offset_months,
            sort_order: t.sort_order,
          });
          if (error) throw error;
        }
      }
    },
    onSuccess: () => {
      notify("Deal types saved", { type: "success" });
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ["deal_types"] });
    },
    onError: () => notify("Failed to save", { type: "error" }),
  });

  const handleChange = (
    index: number,
    field: keyof DealType,
    value: string | number,
  ) => {
    setTypes((prev) =>
      prev.map((t, i) => (i === index ? { ...t, [field]: value } : t)),
    );
    setDirty(true);
  };

  const handleAdd = () => {
    setTypes((prev) => [
      ...prev,
      {
        id: -Date.now(),
        name: "",
        color: "#888888",
        default_duration_months: 12,
        default_rate_per_unit: 0,
        default_closing_offset_months: 3,
        sort_order: prev.length + 1,
      },
    ]);
    setDirty(true);
  };

  const handleDelete = async (index: number) => {
    const t = types[index];
    if (t.id > 0) {
      const { error } = await supabase
        .from("deal_types")
        .delete()
        .eq("id", t.id);
      if (error) {
        notify("Cannot delete — deal type may be in use", { type: "error" });
        return;
      }
    }
    setTypes((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
    queryClient.invalidateQueries({ queryKey: ["deal_types"] });
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Configure deal engagement types. Each type has a color for pipeline
        cards and a default contract duration.
      </p>

      <div className="space-y-3">
        {types.map((t, i) => (
          <div
            key={t.id}
            className="flex items-center gap-3 p-3 border rounded-md"
          >
            <input
              type="color"
              value={t.color}
              onChange={(e) => handleChange(i, "color", e.target.value)}
              className="w-8 h-8 rounded cursor-pointer border-0 p-0"
            />
            <div className="flex-1 grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Name</label>
                <input
                  type="text"
                  value={t.name}
                  onChange={(e) => handleChange(i, "name", e.target.value)}
                  className="w-full px-2 py-1 text-sm border rounded-md"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">
                  Duration (months)
                </label>
                <input
                  type="number"
                  value={t.default_duration_months}
                  onChange={(e) =>
                    handleChange(
                      i,
                      "default_duration_months",
                      Number(e.target.value),
                    )
                  }
                  className="w-full px-2 py-1 text-sm border rounded-md"
                  min={1}
                />
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(i)}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={handleAdd}>
          <Plus className="h-4 w-4 mr-1" />
          Add Deal Type
        </Button>
        {dirty && (
          <Button
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            Save
          </Button>
        )}
      </div>
    </div>
  );
};
